import { useState, useEffect } from 'react';
import { useAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { userAtom, accessTokenAtom } from '../../atoms';

type SearchOption = 'email' | 'referralCode' | 'username' | 'referralLink';

// Extract referral code from a Fujiyama TAP referral link
const extractReferralCodeFromLink = (link: string): string | null => {
    try {
        // Try to extract from URL parameters
        const url = new URL(link);

        // Check for start parameter (Telegram bot link format)
        const startParam = url.searchParams.get('start');
        if (startParam) return startParam;

        // Check for ref or referral parameter (fallback for other formats)
        const refParam = url.searchParams.get('ref') || url.searchParams.get('referral');
        if (refParam) return refParam;

        // Check for path pattern like /ref/CODE or /referral/CODE
        const pathParts = url.pathname.split('/').filter(Boolean);
        const refIndex = pathParts.findIndex(part =>
            part.toLowerCase() === 'ref' ||
            part.toLowerCase() === 'referral'
        );

        if (refIndex >= 0 && refIndex < pathParts.length - 1) {
            return pathParts[refIndex + 1];
        }

        // If no standard pattern is found, try to find any alphanumeric sequence that looks like a code
        const codeMatch = url.pathname.match(/[A-Z0-9]{5,}/i);
        if (codeMatch) return codeMatch[0];

        return null;
    } catch {
        // If the input is not a valid URL, check if it's just the code itself
        const codeMatch = link.match(/[A-Z0-9]{5,}/i);
        return codeMatch ? codeMatch[0] : null;
    }
};

export const CheckChildrenPage = () => {
    const navigate = useNavigate();
    const [user] = useAtom(userAtom);
    const [accessToken] = useAtom(accessTokenAtom);
    const [searchOption, setSearchOption] = useState<SearchOption>('email');
    const [searchValue, setSearchValue] = useState('');
    const [extractedReferralCode, setExtractedReferralCode] = useState<string | null>(null);
    const [result, setResult] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!user) {
            navigate('/dashboard');
            return;
        }

        if (!user.is_debit_card_purchased) {
            navigate('/dashboard');
            return;
        }
    }, [user, navigate]);

    useEffect(() => {
        if (searchOption === 'referralLink' && searchValue) {
            try {
                const code = extractReferralCodeFromLink(searchValue);
                setExtractedReferralCode(code);
                setError(null);
            } catch {
                setExtractedReferralCode(null);
                setError('Invalid Fujiyama TAP Referral URL');
            }
        } else {
            setExtractedReferralCode(null);
        }
    }, [searchValue, searchOption]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        setResult(null);

        // Create request body based on selected search option
        const requestBody: Record<string, string> = {};

        if (searchOption === 'email') {
            requestBody.email = searchValue;
            // Add empty values for other fields
            requestBody.referralCode = '';
            requestBody.username = '';
        } else if (searchOption === 'referralCode') {
            requestBody.referralCode = searchValue;
            // Add empty values for other fields
            requestBody.email = '';
            requestBody.username = '';
        } else if (searchOption === 'referralLink') {
            // Use the already extracted referral code
            if (!extractedReferralCode) {
                setError('Could not extract a valid referral code from the link');
                setIsLoading(false);
                return;
            }

            requestBody.referralCode = extractedReferralCode;
            // Add empty values for other fields
            requestBody.email = '';
            requestBody.username = '';
        } else if (searchOption === 'username') {
            requestBody.username = searchValue;
            // Add empty values for other fields
            requestBody.email = '';
            requestBody.referralCode = '';
        }

        try {
            const response = await fetch('/api/v1/users/children', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.json() as { message?: string };
                throw new Error(errorData.message || 'An error occurred during verification');
            }

            const data = await response.json() as { isChild: boolean };
            setResult(data.isChild);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred during verification');
        } finally {
            setIsLoading(false);
        }
    };

    // Only display if user has purchased a debit card
    if (!user?.is_debit_card_purchased) {
        return (
            <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded-lg shadow-md">
                <h2 className="text-2xl font-bold mb-4 text-center">Check Children</h2>
                <p className="text-center text-red-500 mb-4">
                    You need to purchase a debit card to use this feature.
                </p>
            </div>
        );
    }

    return (
        <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-4 text-center">Check Children</h2>
            <p className="text-gray-600 mb-6 text-center">
                Check if a user is your child
            </p>

            <form onSubmit={handleSubmit}>
                <div className="mb-4">
                    <label htmlFor="searchOption" className="block text-sm font-medium text-gray-700 mb-1">
                        Search by
                    </label>
                    <select
                        id="searchOption"
                        value={searchOption}
                        onChange={(e) => setSearchOption(e.target.value as SearchOption)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="email">Email</option>
                        <option value="referralCode">Fujiyama TAP Referral Code</option>
                        <option value="referralLink">Fujiyama TAP Referral Link</option>
                        <option value="username">Username</option>
                    </select>
                </div>

                <div className="mb-6">
                    <label htmlFor="searchValue" className="block text-sm font-medium text-gray-700 mb-1">
                        {searchOption === 'email' ? 'Email' :
                            searchOption === 'referralCode' ? 'Fujiyama TAP Referral Code' :
                                searchOption === 'referralLink' ? 'Fujiyama TAP Referral Link' : 'Username'}
                    </label>
                    <input
                        type={searchOption === 'email' ? 'email' : 'text'}
                        id="searchValue"
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder={searchOption === 'referralLink' ? "https://t.me/mountfuji4devstgbot?start=XXXXX" : ""}
                        required
                    />
                    {searchOption === 'referralLink' && extractedReferralCode && (
                        <p className="mt-1 text-sm text-green-600">
                            Fujiyama Referral Code: {extractedReferralCode}
                        </p>
                    )}
                </div>

                <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-300"
                >
                    {isLoading ? 'Checking...' : 'Check'}
                </button>
            </form>

            {error && (
                <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-md">
                    {error}
                </div>
            )}

            {result !== null && !error && (
                <div className={`mt-4 p-3 ${result ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'} rounded-md`}>
                    {result
                        ? 'This user is your child.'
                        : 'This user is not your child.'}
                </div>
            )}
        </div>
    );
};

export default CheckChildrenPage;